// © Microsoft Corporation. All rights reserved.

import { classNames } from "@microsoft/fast-web-utilities";
import { chevron_down, enums, hasThemeBase, renderEmojiByType, renderHybrid } from "@msnews/social-utils";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import React from "react";
import MiniReactionBreakdown from "../../common/mini-reaction-breakdown/MiniReactionBreakdown";
import { SocialBarClassNameContract } from "../../SocialBar.classnames";
import { SocialBarLocalizedStrings } from "../../SocialBar.localizedStrings";

export interface MostReactionsProps {
    theme: string;
    t: SocialBarLocalizedStrings;
    managedClasses: SocialBarClassNameContract;
    reactionCounts: any;
    onReactionsClick: (target: any) => void;
    setReactionsCountRef?: (elemet: HTMLElement) => void;
    hideLabel?: boolean;
    customLabel?: string;
    containerClassName?: string;
    telemetryObject?: TelemetryObjectBase;
    hasSeperatorBefore?: boolean;
    hasSeperatorAfter?: boolean;
    responsiveClass?: string;
}

interface MostReactionsState {
    miniReactionBreakdownVisible: boolean;
    isHoverMiniReactionBreakdown: boolean;
}

const maskClassMap = {
    [enums.reactions.angry]: "circleMask",
    [enums.reactions.like]: "likeMask",
    [enums.reactions.love]: "loveMask",
    [enums.reactions.sad]: "circleMask",
    [enums.reactions.surprised]: "circleMask",
    [enums.reactions.thinking]: "circleMask"
};
export default class MostReactions extends React.Component<MostReactionsProps, MostReactionsState> {
    isHoverHandlersAttached = false;

    constructor(props) {
        super(props);

        this.state = {
            miniReactionBreakdownVisible: false,
            isHoverMiniReactionBreakdown: false
        };

    }

    render() {
        const { containerClassName, t, managedClasses, theme, hideLabel, customLabel, telemetryObject, reactionCounts, onReactionsClick, hasSeperatorBefore, hasSeperatorAfter, responsiveClass} = this.props;
        const { miniReactionBreakdownVisible  } = this.state;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);

        // Reaction Counts
        let totalReactionCount = 0;
        let mostReactionKeys = [];
        if (reactionCounts) {
            totalReactionCount = Object.values(reactionCounts as number).reduce(($sum, $count) => $sum + $count, 0);
            mostReactionKeys = Object.keys(reactionCounts)
                .sort(($0, $1) => reactionCounts[$1] - reactionCounts[$0])
                .filter($ => reactionCounts[$] > 0)
                .slice(0, 3);
        }

        return totalReactionCount > 0 && (
            <React.Fragment>
                {miniReactionBreakdownVisible && (
                    <MiniReactionBreakdown
                        counts={reactionCounts}
                        visible={miniReactionBreakdownVisible}
                        onHide={() => this.setState({ miniReactionBreakdownVisible: false })}
                        isHover={this.state.isHoverMiniReactionBreakdown}
                        managedClasses={managedClasses}
                        responsiveClass={responsiveClass}
                    />
                )}

                { hasSeperatorBefore && <span className={managedClasses.seperatorV}>|</span>}

                <button
                    aria-label={`${totalReactionCount} ${t.ProfileReactionsLbl}`}
                    className={classNames(managedClasses.mostReactionsContainer, containerClassName)}
                    onClick={(e) => onReactionsClick(e)}
                    aria-haspopup="true"
                    ref={instance => !(telemetryObject) && this.setRef(instance)}
                    {...(telemetryObject ? renderHybrid(telemetryObject && telemetryObject, false, (el) => this.setRef(el)) : {})}
                >
                    <i className="cwt-icon">
                        {mostReactionKeys.map((key, index) => (
                            <React.Fragment key={key}>
                                {renderEmojiByType(key, index > 0 && managedClasses[maskClassMap[mostReactionKeys[index-1]]])}
                            </React.Fragment>
                        ))}
                        {!hideLabel && <span className="cwt-icon-label">{customLabel || totalReactionCount}</span>}
                    </i>
                </button>

                { hasSeperatorAfter && <span className={managedClasses.seperatorV}>|</span>}

                { !isSuperApp &&
                    <i
                        className="cwt-link cwt-visible-on-focus"
                        role="dialog"
                        aria-haspopup="true"
                        onBlur={() => {
                            this.setState({
                                miniReactionBreakdownVisible: false,
                                isHoverMiniReactionBreakdown: false
                            });
                        }}
                        onClick={() =>
                            this.setState({
                                miniReactionBreakdownVisible: !this.state.miniReactionBreakdownVisible,
                                isHoverMiniReactionBreakdown: false
                            })
                        }>
                        {chevron_down}
                    </i>
                }
            </React.Fragment>
        );
    }

    setRef(element) {
        if (!element) { return; }

        // forward ref
        this.props.setReactionsCountRef && this.props.setReactionsCountRef(element);

        // attach hover handlers
        !this.isHoverHandlersAttached && this.attachHoverHandlers(element);
    }

    attachHoverHandlers(element) {
        if (this.isHoverHandlersAttached) { return; }

        this.isHoverHandlersAttached = true;
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        if (!isSuperApp) {
            element.addEventListener("mouseenter", () => {
                this.setState({
                    miniReactionBreakdownVisible: true,
                    isHoverMiniReactionBreakdown: true
                });
            });
            element.addEventListener("mouseleave", () => {
                this.setState({
                    miniReactionBreakdownVisible: false,
                    isHoverMiniReactionBreakdown: false
                });
            });
        }
    }
}